import type { Feature, FeatureCollection, LineString, Point, Polygon, Position } from 'geojson';
import { v4 as uuidv4 } from 'uuid';

import { bbox, bearing, buffer, centroid, destination, distance, union } from '@turf/turf';

import {
    ARC_STEPS,
    CIRCLE_OPTIONS,
    GEOJSON_FEATURE,
    GEOJSON_FEATURE_TYPE,
    GEOJSON_POLYGON_TYPE,
    GEOJSON_PROPERTY
} from './constants';
import type { Segment } from './types';

const createPointFeature = (coordinates: Position, id: string): Feature<Point> =>
    ({
        geometry: {
            coordinates: coordinates,
            type: GEOJSON_FEATURE_TYPE.POINT
        },
        id: id,
        properties: {},
        type: GEOJSON_FEATURE
    }) as Feature<Point>;

const createTextFeature = (coordinates: Position, id: string, text: string): Feature<Point> =>
    ({
        geometry: {
            coordinates: coordinates,
            type: GEOJSON_FEATURE_TYPE.POINT
        },
        id: id,
        properties: {
            text: text
        },
        type: GEOJSON_FEATURE
    }) as Feature<Point>;

const createLineStringFeature = (coordinates: Position[], id: string): Feature<LineString> =>
    ({
        geometry: {
            coordinates: coordinates,
            type: GEOJSON_FEATURE_TYPE.LINE_STRING
        },
        id: id,
        properties: {},
        type: GEOJSON_FEATURE
    }) as Feature<LineString>;

const createPolygonFeature = (coordinates: Position[][], id: string): Feature<Polygon> =>
    ({
        geometry: {
            coordinates: coordinates,
            type: GEOJSON_FEATURE_TYPE.POLYGON
        },
        id,
        properties: {},
        type: GEOJSON_FEATURE
    }) as Feature<Polygon>;

const generatePolygonFromVertices = (vertexFeatures: Feature<Point>[], id: string) => {
    const finalPolygonCoords = [
        [
            ...vertexFeatures.map(feature => feature.geometry.coordinates),
            vertexFeatures[0].geometry.coordinates
        ]
    ];

    return createPolygonFeature(finalPolygonCoords, id);
};

const hasFeatureWithId = (features?: Feature[], id?: string): boolean => {
    if (!features || !id) {
        return false;
    }

    return features.some(feature => feature.id === id);
};

const getFeatureIds = (features: Feature[] | undefined): string[] => {
    if (!features) {
        return [];
    }

    return features
        .map(feature => feature.id as string)
        .filter((id): id is string => id !== undefined);
};

const hasAnyFeatureWithIds = (features: Feature[] | undefined, ids: string[]): boolean => {
    if (!features || ids.length === 0) {
        return false;
    }

    const featureIds = getFeatureIds(features);

    return ids.some(id => featureIds.includes(id));
};

const findFeatureWithId = (features?: Feature[], id?: string): Feature<Polygon> | undefined => {
    if (!features || !id) {
        return undefined;
    }

    return features.find(feature => feature.id === id) as Feature<Polygon>;
};

const isCircle = (feature: Feature<Polygon>): boolean =>
    feature.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE] === GEOJSON_POLYGON_TYPE.CIRCLE;

const getClosestFeatureToPoint = (
    featuresUnderClick: Feature[],
    featuresToCheck: Feature[],
    latlng: Position
): Feature<Polygon> | undefined => {
    const featuresToCheckIds = new Set(featuresToCheck.map(polygon => polygon.id));
    const clickedFeaturesToCheck = featuresUnderClick.filter(feature =>
        featuresToCheckIds.has(feature.id)
    );

    let closestPolygon: Feature<Polygon> | undefined;
    let minDistance = Infinity;

    for (const clickedFeature of clickedFeaturesToCheck) {
        const matchingFeature = featuresToCheck.find(polygon => polygon.id === clickedFeature.id);

        if (!matchingFeature) {
            continue;
        }

        const obstacleCentroid = centroid(matchingFeature);
        const dist = distance(latlng, obstacleCentroid, CIRCLE_OPTIONS);

        if (dist < minDistance) {
            minDistance = dist;
            closestPolygon = matchingFeature as Feature<Polygon>;
        }
    }

    return closestPolygon;
};

const calculateBoundingBoxCoordinates = (
    feature: Feature,
    bufferMeters?: number
): [number, number][] => {
    const featureCopy = {
        geometry: {
            coordinates: (feature.geometry as Polygon).coordinates,
            type: feature.geometry.type
        },
        id: feature.id,
        properties: feature.properties,
        type: feature.type
    } as Feature<Polygon>;

    let featureToUse = featureCopy as Feature<Polygon>;

    if (bufferMeters !== undefined && featureToUse.geometry.coordinates[0].length >= 4) {
        const bufferedFeature = buffer(featureToUse, bufferMeters, {
            units: 'meters'
        }) as Feature<Polygon>;

        if (bufferedFeature) {
            featureToUse = bufferedFeature;
        }
    }

    const [minLng, minLat, maxLng, maxLat] = bbox(featureToUse);

    return [
        [minLng, maxLat],
        [maxLng, maxLat],
        [maxLng, minLat],
        [minLng, minLat],
        [minLng, maxLat]
    ];
};

const getEdgeMidpoint = (start: Position, end: Position): Position => [
    (start[0] + end[0]) / 2,
    (start[1] + end[1]) / 2
];

type NorthmostLinePlacement = {
    position: Position;
    rotation: number;
};

const getNorthmostLinePlacement = (
    coordinates: Position[],
    feature?: Feature
): NorthmostLinePlacement => {
    if (coordinates.length < 2) {
        return { position: coordinates[0], rotation: 0 };
    }

    let northmostMidpoint = getEdgeMidpoint(coordinates[0], coordinates[1]);
    let northmostStart = coordinates[0];
    let northmostEnd = coordinates[1];

    for (let index = 1; index < coordinates.length - 1; index++) {
        const midpoint = getEdgeMidpoint(coordinates[index], coordinates[index + 1]);

        if (midpoint[1] > northmostMidpoint[1]) {
            northmostMidpoint = midpoint;
            northmostStart = coordinates[index];
            northmostEnd = coordinates[index + 1];
        }
    }

    const lineBearing = bearing(northmostStart, northmostEnd);
    const rotationClockwise = lineBearing + 90;
    const rotationCounterClockwise = lineBearing - 90;

    const rawRotation =
        feature?.geometry?.type === 'Polygon'
            ? getOutwardFacingRotation(
                  northmostMidpoint,
                  rotationClockwise,
                  rotationCounterClockwise,
                  feature
              )
            : rotationClockwise;

    const rotation = clampRotationToUpright(rawRotation);

    return { position: northmostMidpoint, rotation };
};

const clampRotationToUpright = (rotation: number): number => {
    let normalized = ((rotation % 360) + 360) % 360;

    if (normalized > 180) {
        normalized -= 360;
    }

    if (normalized > 90) {
        return normalized - 180;
    }

    if (normalized < -90) {
        return normalized + 180;
    }

    return normalized;
};

const getOutwardFacingRotation = (
    edgeMidpoint: Position,
    rotationA: number,
    rotationB: number,
    feature: Feature
): number => {
    const polygonCentroid = centroid(feature).geometry.coordinates;
    const bearingToInterior = bearing(edgeMidpoint, polygonCentroid);
    const bearingToExterior = (bearingToInterior + 180) % 360;
    const directionA = (rotationA + 360) % 360;
    const directionB = (rotationB + 360) % 360;
    const exteriorDirection = (bearingToExterior + 360) % 360;

    const diffA = Math.min(
        Math.abs(directionA - exteriorDirection),
        360 - Math.abs(directionA - exteriorDirection)
    );
    const diffB = Math.min(
        Math.abs(directionB - exteriorDirection),
        360 - Math.abs(directionB - exteriorDirection)
    );

    return diffA < diffB ? rotationA : rotationB;
};

const getGeometryOutlineCoordinates = (feature: Feature): Position[] => {
    const { geometry } = feature;
    const { coordinates, type } = geometry as Polygon | LineString;

    if (type === 'Polygon') {
        return coordinates[0];
    }

    if (type === 'LineString') {
        return coordinates;
    }

    return calculateBoundingBoxCoordinates(feature);
};

const createLinePolygonWithWidthMeters = (feature: Feature<LineString>, widthMeters: number) => {
    const { coordinates } = feature.geometry;

    const lineSegments = segment(coordinates);
    const rectangles = convertSegmentsToRectangles(lineSegments, widthMeters);

    if (rectangles.features.length < 2) {
        return rectangles.features[0] as Feature<Polygon>;
    }

    const arcs = convertSegmentsToArcs(coordinates, widthMeters);

    rectangles.features.push(...arcs);

    return union(rectangles) as Feature<Polygon>;
};

const convertSegmentsToArcs = (coordinates: Position[], widthMeters: number) => {
    const arcPolygons = [];

    for (let i = 0; i < coordinates.length - 2; i++) {
        const startCoordinate = coordinates[i];
        const midpointCoordinate = coordinates[i + 1];
        const endCoordinate = coordinates[i + 2];

        const startToMidpointBearing = bearing(startCoordinate, midpointCoordinate) - 90;
        const midpointToEndBearing = bearing(midpointCoordinate, endCoordinate) - 90;

        const stepDegrees = (startToMidpointBearing - midpointToEndBearing) / ARC_STEPS;

        const createArc = (bearingOffset: number = 0) => {
            const points = [midpointCoordinate];

            for (let j = 0; j <= ARC_STEPS; j++) {
                const bearing = startToMidpointBearing - stepDegrees * j + bearingOffset;
                const point = destination(midpointCoordinate, widthMeters, bearing, CIRCLE_OPTIONS)
                    .geometry.coordinates;

                points.push(point);
            }

            points.push(midpointCoordinate);

            return createPolygonFeature([points], uuidv4());
        };

        arcPolygons.push(createArc());
        arcPolygons.push(createArc(180));
    }

    return arcPolygons;
};

const convertSegmentsToRectangles = (lineSegments: Segment[], widthMeters: number) => {
    const rectangles = [];

    for (const lineSegment of lineSegments) {
        const rectangle = convertSegmentToRectangle(lineSegment, widthMeters);

        rectangles.push(rectangle);
    }

    return {
        features: rectangles,
        type: 'FeatureCollection'
    } as FeatureCollection<Polygon>;
};

const convertSegmentToRectangle = (lineSegment: Segment, widthMeters: number) => {
    const bearingDegrees = bearing(lineSegment.start, lineSegment.end);

    const firstSide = {
        end: destination(lineSegment.start, widthMeters, bearingDegrees - 90, CIRCLE_OPTIONS)
            .geometry.coordinates,
        start: destination(lineSegment.start, widthMeters, bearingDegrees + 90, CIRCLE_OPTIONS)
            .geometry.coordinates
    };

    const secondSide = {
        end: destination(lineSegment.end, widthMeters, bearingDegrees - 90, CIRCLE_OPTIONS).geometry
            .coordinates,
        start: destination(lineSegment.end, widthMeters, bearingDegrees + 90, CIRCLE_OPTIONS)
            .geometry.coordinates
    };

    const rectangleCoords = [
        firstSide.start,
        secondSide.start,
        secondSide.end,
        firstSide.end,
        firstSide.start
    ];

    return createPolygonFeature([rectangleCoords], uuidv4());
};

const segment = (coordinates: Position[]): Segment[] => {
    const lineSegments = [];

    for (let i = 0; i < coordinates.length - 1; i++) {
        const currentCoordinate = coordinates[i];
        const nextCoordinate = coordinates[i + 1];

        lineSegments.push({
            end: nextCoordinate,
            start: currentCoordinate
        });
    }

    return lineSegments;
};

export const featureUtil = {
    calculateBoundingBoxCoordinates,
    createLinePolygonWithWidthMeters,
    createLineStringFeature,
    createPointFeature,
    createPolygonFeature,
    createTextFeature,
    findFeatureWithId,
    generatePolygonFromVertices,
    getClosestFeatureToPoint,
    getFeatureIds,
    getGeometryOutlineCoordinates,
    getNorthmostLinePlacement,
    hasAnyFeatureWithIds,
    hasFeatureWithId,
    isCircle
};
